<script lang="ts">
    import { onMount } from "svelte"
    import BarLoader from "svelte-loading-spinners/BarLoader.svelte"
    import { getCachedEntries, getDBEntry, postProductViewEvent } from "../../api"
    import { langField, location } from "../../i18n"
    import { get } from "svelte/store"
    import { login } from "../../functionTools/login"
    import { getProductPrice } from "../../utils"
    import { defaultVAT } from "../../../../api/hooks/config-client"
    import { productToInput, trackItemsEvent } from "../../lib/ga"
    import CustomerReviews from "../ecommerce/product/detailed/CustomerReviews.svelte"
    import ProductDetails from "../ecommerce/product/detailed/ProductDetails.svelte"
    import ProductDetailsGrid from "../ecommerce/product/detailed/ProductDetailsGrid.svelte"
    import ProductHighlightList from "../ecommerce/product/detailed/ProductHighlightList.svelte"
    import ProductImageSlider from "../ecommerce/product/detailed/productImage/ProductImageSlider.svelte"
    import StaticProductProperties from "../ecommerce/product/detailed/StaticProductProperties.svelte"
    import ContentBlock from "../pagebuilder/ContentBlock.svelte"
    import Index from "../SEO/Index.svelte"
    import NotFound from "./NotFound.svelte"
    import RelatedProductsWrapper from "../pagebuilder/RelatedProductsWrapper.svelte"
    import { isEuCountry } from "../../europe"
    let { locationPath }: { locationPath: string } = $props()
    locationPath = typeof window !== "undefined" ? window.location.pathname : locationPath
    const segments = locationPath.split("/").filter(Boolean)

    const codeWithSlug = [...segments].reverse().find((seg) => /^[\d-]+/.test(seg)) ?? ""

    const code = codeWithSlug.split("_")[0] || ""
    let product: Product = $state()
    let loading = $state(true)
    let preBlocks: ContentBlockT[] = $state([])
    let postBlocks: ContentBlockT[] = $state([])

    getDBEntry("product", { code }, "full").then((res) => {
        if (!res) {
            loading = false
            return
        }
        product = res
        postProductViewEvent(product)
        // GA4 view_item (Preis brutto wie Matomo/purchase)
        trackItemsEvent(
            "view_item",
            [productToInput(product, getProductPrice(product, !!get(login)))],
            get(login) ? 0 : defaultVAT
        )
        loading = false
        getCachedEntries("content", {
            "generalDetails.type": "preAndPostContentBlocks",
            $or: [
                {
                    "generalDetails.productFilter.type": "allProducts",
                },
                {
                    "generalDetails.productFilter.type": "specificProducts",
                    "generalDetails.productFilter.products": code.split("-")[0],
                },
                {
                    "generalDetails.productFilter.type": "tags",
                    "generalDetails.productFilter.tags": { $in: product.tags },
                },
            ],
        }).then((res) => {
            const contentEntries = res as ContentEntry<"preAndPostContentBlocks">[]
            const sortedEntriesHighestFirst = contentEntries.sort((a, b) => {
                return b.generalDetails.productFilter.priority - a.generalDetails.productFilter.priority
            })
            sortedEntriesHighestFirst.forEach((entry) => {
                preBlocks.push(...(entry.preMainProductRows.blocks || []))
                postBlocks.push(...(entry.postMainProductRows.blocks || []))
            })
        })
    })
    function returnMetaDescription(product: TProduct): string {
        const productName = langField(product.name, "")
        const parts = [`${productName} von Renz`]
        const productionCountryCode = (product.production_country || "").trim().toUpperCase()
        const showProductionCountryBadge =
            !productionCountryCode || productionCountryCode === "EU" || isEuCountry(productionCountryCode)

        if (product.price_net_end_customer) {
            parts.push(`ab ${product.price_net_end_customer.toFixed(2)} EUR netto`)
        }

        if (product.gtin) {
            parts.push(`GTIN: ${product.gtin}`)
        }

        if (showProductionCountryBadge) {
            parts.push(`Made in ${productionCountryCode || "EU"}. Direkt vom Hersteller kaufen.`)
        } else {
            parts.push("Direkt vom Hersteller kaufen.")
        }

        return parts.join(" | ")
    }

    let setActiveImage: (idx: number) => void = $state()
    let hasPostBlock = $derived(postBlocks.length || (product?.related_codes && product.related_codes.length > 0))
    let isMobileProductLayout = $state(false)

    onMount(() => {
        const mediaQuery = window.matchMedia("(max-width: 1600px)")
        const updateLayout = () => {
            isMobileProductLayout = mediaQuery.matches
        }

        updateLayout()
        mediaQuery.addEventListener("change", updateLayout)

        return () => {
            mediaQuery.removeEventListener("change", updateLayout)
        }
    })
</script>

{#if preBlocks.length}
    <div class="content-block-wrapper">
        {#each preBlocks as block}
            <ContentBlock {block} />
        {/each}
    </div>
{/if}
{#if loading}
    <BarLoader />
{:else if !!product}
    <Index
        createdAt={new Date(product.updateTime)}
        updatedAt={new Date(product.updateTime)}
        metaDescription={returnMetaDescription(product)}
        title={langField(product.name)}
        {product}
    />
    <section class={hasPostBlock ? "has-post-blocks" : "has-no-post-blocks"}>
        <div class="left-col">
            <div class="indentated">
                <ProductImageSlider
                    {product}
                    setActiveImageCallback={(fn: (idx: number) => void) => {
                        setActiveImage = fn
                    }}
                />
                <div class="only-mobile">
                    <ProductDetails {product} {setActiveImage} headingTag={isMobileProductLayout ? "h1" : "div"} />
                </div>
                <p id="product-description">
                    {@html langField(product.description, "")}
                </p>
                <ProductHighlightList {product} />
                <ProductDetailsGrid {product} />
                <CustomerReviews {product} />
                <StaticProductProperties />
            </div>
        </div>
        <div class="right-col">
            <div class="sticky-wrapper">
                <div class="inner-sticky-wrapper">
                    <ProductDetails {product} {setActiveImage} headingTag={isMobileProductLayout ? "div" : "h1"} />
                    <div></div>
                </div>
            </div>
        </div>
    </section>
{:else}
    <NotFound />
{/if}
{#if hasPostBlock}
    <div class="content-block-wrapper">
        {#if product.related_codes && product.related_codes.length > 0}
            <RelatedProductsWrapper {product} />
        {/if}
        {#if postBlocks.length}
            {#each postBlocks as block}
                <ContentBlock {block} />
            {/each}
        {/if}
    </div>
{/if}

<style lang="less">
    @import "../../css/variables.less";
    section {
        display: flex;
        max-width: var(--max-page-width);
        gap: 1rem;
        margin-top: 1px;
        &.has-no-post-blocks {
            margin-bottom: 2.4rem;
        }
        @media (max-width: 1600px) {
            display: block;
            padding: 0px var(--default-horizontal-padding);
        }
        .left-col {
            flex-grow: 1;
            .only-mobile {
                display: none;
            }

            .indentated {
                padding-left: var(--default-horizontal-padding);
                @media (min-width: 1920px) {
                    padding-left: 0px;
                    margin-left: calc((min(var(--max-page-width), 100vw) - var(--max-container-width)) / 2);
                }
                p {
                    white-space: pre-wrap;
                    padding: 3.6rem 0px;
                }
                @media @max-mobile-product {
                    padding-left: 0px;
                    margin: 0px;
                    width: 100%;
                    .only-mobile {
                        display: block;
                        width: 100%;
                    }
                }
            }
        }
        .right-col {
            @media @max-mobile-product {
                display: none;
            }
            padding: 2.4rem;
            padding-left: 1.2rem;
            padding-right: 0px;
            position: relative;

            .sticky-wrapper {
                position: sticky;
                top: 156px;
                padding-right: var(--default-horizontal-padding);
                width: 100%;
                overflow-y: auto;
                height: min(calc(100vh - 156px), 2400px);
                width: 100%;
                @media (min-width: calc(1776px + 3.6rem )) {
                    padding-right: 0px;
                    padding-right: calc((min(var(--max-page-width), 100vw) - var(--max-container-width)) / 2);
                }

                .inner-sticky-wrapper {
                    @media (max-width: 1900px) {
                        width: 600px;
                        min-width: 600px;
                    }
                    @media (min-width: 1900px) {
                        width: 650px;
                        min-width: 650px;
                    }
                    height: 100%;
                }

                // scrollbar styling
                &::-webkit-scrollbar {
                    display: none;
                }
                &::-webkit-scrollbar-thumb {
                    background: transparent;
                }
                &::-webkit-scrollbar-track {
                    background: transparent;
                }
            }
        }
    }
</style>
